import { useRef, useCallback } from 'react';
import { 
  KnowledgeChatService, 
  KnowledgeContext,
  KnowledgeChatMessage
} from '../services/knowledgeChatService';

interface BackgroundChatSession {
  service: KnowledgeChatService;
  isConnected: boolean;
  isReady: boolean;
  knowledgeItemId: string;
  knowledgeContext: KnowledgeContext;
  cachedHistory?: KnowledgeChatMessage[]; // Кеш загруженной истории
}

/**
 * Хук для фоновой инициализации чатов базы знаний
 * Позволяет заранее подготовить соединение и загрузить историю чата
 * для мгновенного отображения при открытии чата пользователем
 */
export const useBackgroundKnowledgeChat = () => {
  const backgroundSessionsRef = useRef<Map<string, BackgroundChatSession>>(new Map());

  /**
   * Инициализирует чат фоново для заданного документа
   */
  const initializeBackgroundChat = useCallback(async (
    knowledgeItemId: string, 
    knowledgeContext: KnowledgeContext
  ) => {
    // Проверяем, нет ли уже активной сессии для этого документа
    const existingSession = backgroundSessionsRef.current.get(knowledgeItemId);
    if (existingSession && existingSession.isReady) {
      return existingSession;
    }

    try {
      
      // Создаем новый сервис чата
      const chatService = new KnowledgeChatService();
      
      // Создаем сессию в статусе "подключается"
      const session: BackgroundChatSession = {
        service: chatService,
        isConnected: false,
        isReady: false,
        knowledgeItemId,
        knowledgeContext
      };
      
      // Сохраняем сессию
      backgroundSessionsRef.current.set(knowledgeItemId, session);

      // Настраиваем обработчики событий для сервиса
      chatService.onMessageReceived((message) => {
      });

      chatService.onStatusChanged((statusEvent) => {
      });

      // Подключаемся к SignalR Hub
      await chatService.connect();
      session.isConnected = true;

      // Загружаем историю сообщений фоново
      try {
        const history = await chatService.getChatHistory(knowledgeItemId);
        session.cachedHistory = history; // Сохраняем историю в кеш
        session.isReady = true;
      } catch (historyError) {
        // Даже если история не загрузилась, помечаем сессию как готовую для отправки сообщений
        session.isReady = true;
      }

      return session;
      
    } catch (error) {
      // Удаляем неудачную сессию
      backgroundSessionsRef.current.delete(knowledgeItemId);
      throw error;
    }
  }, []);

  /**
   * Получает готовую фоновую сессию чата для использования
   * НЕ удаляет сессию из кеша - это будет сделано явно при закрытии чата
   */
  const getBackgroundChatService = useCallback((knowledgeItemId: string): KnowledgeChatService | null => {
    const session = backgroundSessionsRef.current.get(knowledgeItemId);
    
    if (!session || !session.isReady) {
      return null;
    }

    return session.service;
  }, []);

  /**
   * Получает частично готовую фоновую сессию (с подключением, но возможно без загруженной истории)
   */
  const getPartialBackgroundChatService = useCallback((knowledgeItemId: string): KnowledgeChatService | null => {
    const session = backgroundSessionsRef.current.get(knowledgeItemId);
    
    if (!session || !session.isConnected) {
      return null;
    }

    return session.service;
  }, []);

  /**
   * УСТАРЕВШИЙ: Передает готовую фоновую сессию основному хуку чата
   * Возвращает null если сессия не готова
   */
  const takeBackgroundSession = useCallback((knowledgeItemId: string): KnowledgeChatService | null => {
    return getBackgroundChatService(knowledgeItemId);
  }, [getBackgroundChatService]);

  /**
   * УСТАРЕВШИЙ: Передает частично готовую фоновую сессию
   */
  const takePartialBackgroundSession = useCallback((knowledgeItemId: string): KnowledgeChatService | null => {
    return getPartialBackgroundChatService(knowledgeItemId);
  }, [getPartialBackgroundChatService]);

  /**
   * Очищает фоновую сессию для документа
   */
  const cleanupBackgroundSession = useCallback((knowledgeItemId: string) => {
    const session = backgroundSessionsRef.current.get(knowledgeItemId);
    if (session) {
      session.service.disconnect().catch(console.warn);
      backgroundSessionsRef.current.delete(knowledgeItemId);
    }
  }, []);

  /**
   * Очищает все фоновые сессии
   */
  const cleanupAllBackgroundSessions = useCallback(() => {
    
    backgroundSessionsRef.current.forEach((session, knowledgeItemId) => {
      session.service.disconnect().catch(console.warn);
    });
    
    backgroundSessionsRef.current.clear();
  }, []);

  /**
   * Получает кешированную историю сообщений из фоновой сессии
   */
  const getCachedHistory = useCallback((knowledgeItemId: string): KnowledgeChatMessage[] | null => {
    const session = backgroundSessionsRef.current.get(knowledgeItemId);
    
    if (!session || !session.cachedHistory) {
      return null;
    }

    return session.cachedHistory;
  }, []);

  /**
   * Проверяет готовность фоновой сессии
   */
  const isBackgroundSessionReady = useCallback((knowledgeItemId: string): boolean => {
    const session = backgroundSessionsRef.current.get(knowledgeItemId);
    return session ? session.isReady : false;
  }, []);

  return {
    initializeBackgroundChat,
    getBackgroundChatService,
    getPartialBackgroundChatService,
    takeBackgroundSession,
    takePartialBackgroundSession,
    cleanupBackgroundSession,
    cleanupAllBackgroundSessions,
    isBackgroundSessionReady,
    getCachedHistory
  };
};